import React, { useMemo } from 'react';

import { useFeatures } from '@lifeomic/phc-web-toolkit/src/hooks/useFeatures';
import {
  AutoSuggest,
  AutoSuggestListItem,
} from '@lifeomic/phc-web-toolkit/src/components/AutoSuggest';
import { GlobalHotKeys, KeyMap } from 'react-hotkeys';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Redirect } from 'react-router-dom';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { AutoSuggestProps } from '@lifeomic/phc-web-toolkit/src/components/AutoSuggest/AutoSuggest';
import {
  isFeatureSuggestion,
  isRouteSuggestion,
  isSubjectSuggestion,
  Suggestion,
} from '@lifeomic/phc-web-toolkit/src/components/Omnibar/types';
import { useFeatureSuggestions } from '@lifeomic/phc-web-toolkit/src/components/Omnibar/useFeatureSuggestions';
import {
  OmnibarProvider,
  useOmnibarContext,
} from '@lifeomic/phc-web-toolkit/src/components/Omnibar/OmnibarProvider';
import {
  restoreAppBars,
  toggleAppBarsHidden,
} from '@lifeomic/phc-web-toolkit/src/util/appBarsVisibility';

const keyMap: KeyMap = {
  OMNIBAR: ['o', 'meta+o', 'ctrl+o', 'meta+k', 'ctrl+k'],
  HIDE_SIDEBAR: ['meta+shift+f'],
  RESTORE_BARS: ['esc'],
};

const useAutoSuggestTheme = makeStyles({
  suggestionsContainer: {
    width: '100%',
    maxWidth: '22rem',
  },
});

export { OmnibarProvider };

export const Omnibar: React.FC = () => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [redirectUrl, setRedirectUrl] = React.useState<string | undefined>();
  const autoSuggestTheme = useAutoSuggestTheme();

  const hasFeature = useFeatures();
  const hasOmnibarManageToggles = hasFeature('omnibarManageToggles');

  React.useEffect(() => {
    if (redirectUrl) setRedirectUrl(undefined);
  }, [redirectUrl]);

  const onClose = (): void => {
    setIsOpen(false);
    setRedirectUrl(undefined);
  };

  const [isOpening, setIsOpening] = React.useState<boolean>(true);

  const handlers = {
    OMNIBAR: (): void => {
      setIsOpen(true);
    },
    HIDE_SIDEBAR: (): void => {
      toggleAppBarsHidden();
    },
    RESTORE_BARS: (): void => {
      restoreAppBars();
    },
  };

  const {
    suggestions: registeredSuggestions,
    getHandler,
    searchValue,
    setSearchValue,
    loading,
  } = useOmnibarContext();

  const handleChange: AutoSuggestProps['onChange'] = (
    value: string,
    { method },
  ): void => {
    // keyboard navigation should not update the search value
    if (method === 'down' || method === 'up') return;
    setSearchValue(value);
  };

  // this is a hack to delay rendering the text input until after the modal is open
  // because otherwise the text input will focus and get the keyboard key (o) from the trigger
  // even when using `stopEventPropagationAfterHandling`
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setIsOpening(!isOpen);
    }, 200);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  const featureMatches = useFeatureSuggestions(searchValue);

  const suggestions = useMemo(() => {
    return [
      ...(hasOmnibarManageToggles ? featureMatches : []),
      ...registeredSuggestions,
      // Add Global Suggestions here
    ];
  }, [hasOmnibarManageToggles, featureMatches, registeredSuggestions]);

  const handleSuggestionSelected = ({
    suggestion,
  }: {
    suggestion: Suggestion;
  }): void => {
    // Check if this is a registered suggestion with a custom handler
    const registeredSuggestion = registeredSuggestions.find(
      s => s === suggestion,
    );
    if (registeredSuggestion && 'id' in registeredSuggestion) {
      const customHandler = getHandler(registeredSuggestion.id);
      if (customHandler) {
        customHandler(suggestion, setRedirectUrl);
        setIsOpen(false);
        return;
      }
    }

    // Default handling for built-in suggestion types
    if (isFeatureSuggestion(suggestion)) {
      const feature = suggestion.value;
      (window as any).LifeOmic.util.toggleFeature(feature);
    } else if (isRouteSuggestion(suggestion)) {
      setRedirectUrl(suggestion.path);
    }
    // Handle any additional registered suggestions that might be route-like
    // but not caught by the type guards above
    if (
      !isFeatureSuggestion(suggestion) &&
      !isSubjectSuggestion(suggestion) &&
      'path' in suggestion &&
      typeof (suggestion as any).path === 'string'
    ) {
      setRedirectUrl((suggestion as any).path);
    }
    // Add Suggestion Selection handling here
    setIsOpen(false);
  };

  const renderSuggestion = (
    { display }: Suggestion,
    isHighlighted: boolean,
  ): JSX.Element => {
    return (
      <AutoSuggestListItem isHighlighted={isHighlighted} title={display} />
    );
  };

  if (redirectUrl) return <Redirect push to={redirectUrl} />;

  return (
    <GlobalHotKeys keyMap={keyMap} handlers={handlers}>
      <Modal isOpen={isOpen} onDismiss={onClose} size={1} customHeader={<></>}>
        {isOpening ? null : (
          <AutoSuggest
            fetchSuggestions={() => void 0} // we handle it ourselves
            getSuggestionValue={(suggestion: Suggestion) => suggestion.value}
            onSuggestionSelected={handleSuggestionSelected}
            renderSuggestion={renderSuggestion}
            suggestions={suggestions}
            value={searchValue}
            onChange={handleChange}
            isLoading={loading}
            autoSuggestTheme={autoSuggestTheme}
            testIds={{ input: 'omnibar-search' }}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={true}
          />
        )}
      </Modal>
    </GlobalHotKeys>
  );
};
